import { useCallback, useEffect, useMemo, useState } from 'react'
import { deserializeProject } from '@rawgraphs/rawgraphs-core'
import { BRAND_CONFIG } from '../branding/brandConfig'
import useDebounce from './useDebounce'
import { SESSION_SAVE_DEBOUNCE_MS } from '../config/workflow'

function getSessionStatusLabel(status, savedAt) {
  if (status === 'restoring') return 'Restoring session…'
  if (status === 'saving') return 'Autosaving…'
  if (status === 'restored') return 'Previous session restored'
  if (status === 'error') return 'Autosave unavailable'
  if (status === 'saved' && savedAt) {
    return `Autosaved ${savedAt.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    })}`
  }
  return 'Autosave on'
}

export default function useProjectSession({
  baseCharts,
  importProject,
  exportProject,
  canPersistSession,
  sessionSnapshot,
  brandTheme,
  setBrandTheme,
}) {
  const [sessionStatus, setSessionStatus] = useState('idle')
  const [sessionSavedAt, setSessionSavedAt] = useState(null)
  const [sessionBootstrapComplete, setSessionBootstrapComplete] = useState(false)

  const debouncedSessionSnapshot = useDebounce(
    sessionSnapshot,
    SESSION_SAVE_DEBOUNCE_MS
  )

  useEffect(() => {
    const projectUrlStr = new URLSearchParams(window.location.search).get('url')
    if (projectUrlStr) {
      setSessionBootstrapComplete(true)
      return
    }

    const rawSession = localStorage.getItem(BRAND_CONFIG.storage.sessionKey)
    if (!rawSession) {
      setSessionBootstrapComplete(true)
      return
    }

    let isCancelled = false

    async function restoreSession() {
      setSessionStatus('restoring')
      try {
        const parsedSession = JSON.parse(rawSession)
        const savedProject = parsedSession?.project
        if (!savedProject) {
          throw new Error('Missing saved project payload')
        }

        const restoredProject =
          typeof savedProject === 'string'
            ? deserializeProject(savedProject, baseCharts)
            : savedProject
        await importProject(restoredProject)

        if (isCancelled) {
          return
        }

        if (parsedSession.brandTheme) {
          setBrandTheme((currentTheme) => ({
            ...currentTheme,
            ...parsedSession.brandTheme,
          }))
        }
        if (parsedSession.savedAt) {
          const restoredAt = new Date(parsedSession.savedAt)
          if (!Number.isNaN(restoredAt.getTime())) {
            setSessionSavedAt(restoredAt)
          }
        }

        setSessionStatus('restored')
      } catch (error) {
        console.error('Could not restore session', error)
        localStorage.removeItem(BRAND_CONFIG.storage.sessionKey)
        setSessionStatus('error')
      } finally {
        if (!isCancelled) {
          setSessionBootstrapComplete(true)
        }
      }
    }

    restoreSession()

    return () => {
      isCancelled = true
    }
  }, [
    baseCharts,
    importProject,
    setBrandTheme,
  ])

  useEffect(() => {
    if (!sessionBootstrapComplete) {
      return undefined
    }

    if (!canPersistSession) {
      return undefined
    }

    let isCancelled = false

    async function persistSession() {
      setSessionStatus((currentStatus) =>
        currentStatus === 'restoring' ? currentStatus : 'saving'
      )

      try {
        const serializedProject = await exportProject()
        if (isCancelled || !serializedProject) return

        const savedAt = new Date()
        localStorage.setItem(
          BRAND_CONFIG.storage.sessionKey,
          JSON.stringify({
            project: serializedProject,
            brandTheme,
            savedAt: savedAt.toISOString(),
          })
        )
        setSessionSavedAt(savedAt)
        setSessionStatus('saved')
      } catch (error) {
        console.error('Could not autosave session', error)
        if (!isCancelled) {
          setSessionStatus('error')
        }
      }
    }

    persistSession()

    return () => {
      isCancelled = true
    }
  }, [
    canPersistSession,
    sessionBootstrapComplete,
    debouncedSessionSnapshot,
    exportProject,
    brandTheme,
  ])

  const clearSavedSession = useCallback(() => {
    localStorage.removeItem(BRAND_CONFIG.storage.sessionKey)
    setSessionSavedAt(null)
    setSessionStatus('idle')
  }, [])

  const sessionStatusLabel = useMemo(
    () => getSessionStatusLabel(sessionStatus, sessionSavedAt),
    [sessionStatus, sessionSavedAt]
  )

  return {
    sessionStatus,
    sessionSavedAt,
    sessionBootstrapComplete,
    clearSavedSession,
    sessionStatusLabel,
  }
}
